/** Planning-only editor for an individual work-order task and its checklist. */
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "@/api/problem";
import { type Task, useSaveTaskDefinition } from "@/features/workorders/queries";
import { ChecklistEditor, type ChecklistRow } from "@/shared/components/checklist-editor";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Textarea } from "@/shared/ui/form";
import { Dialog } from "@/shared/ui/primitives";

/** Keep task planning available only while a work order is open for scope changes. */
export function canPlanTasks(status: string): boolean {
  return ["draft", "planned", "scheduled", "in_progress", "on_hold"].includes(status);
}

/** Protect completed tasks and any pending task that already contains execution evidence. */
export function isTaskUntouched(task: Task): boolean {
  return (
    task.status === "pending" &&
    !task.completed_at &&
    !task.notes &&
    task.checklist.every(
      (item) => !item.answered_at && item.value == null && item.passed == null && !item.note,
    )
  );
}

/** Add or edit a work-order-local task without changing its originating template. */
export function TaskDefinitionDialog({
  woId,
  task,
  onClose,
}: {
  woId: string;
  task?: Task;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveTaskDefinition(woId);
  const [title, setTitle] = useState(task?.title ?? "");
  const [instructions, setInstructions] = useState(task?.instructions ?? "");
  const [mandatory, setMandatory] = useState(task?.is_mandatory ?? true);
  const [checklist, setChecklist] = useState<ChecklistRow[]>(
    () =>
      task?.checklist.map((item) => {
        const options = item.constraints.choices ?? item.constraints.options;
        return {
          ...item,
          _id: item.id,
          _options: Array.isArray(options)
            ? options.filter((option): option is string => typeof option === "string").join(", ")
            : "",
        };
      }) ?? [],
  );

  const submit = (event: React.SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    save.mutate(
      {
        taskId: task?.id,
        body: {
          title: title.trim(),
          instructions,
          is_mandatory: mandatory,
          checklist: checklist.map((item) => ({
            id: item.id,
            prompt: item.prompt.trim(),
            response_type: item.response_type,
            is_mandatory: item.is_mandatory,
            photo_required: item.photo_required,
            constraints:
              item.response_type === "choice"
                ? {
                    ...item.constraints,
                    choices: (item._options ?? "")
                      .split(",")
                      .map((option) => option.trim())
                      .filter(Boolean),
                  }
                : item.constraints,
          })),
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open && !save.isPending) onClose();
      }}
      title={t(task ? "workOrders.editTask" : "workOrders.addTask")}
      description={t("workOrders.taskLocalOnly")}
    >
      <form onSubmit={submit} className="space-y-4">
        <fieldset disabled={save.isPending} className="min-w-0 space-y-4">
          <Field id="task-title" label={t("app.title")}>
            <Input
              id="task-title"
              name="title"
              value={title}
              maxLength={200}
              required
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
          <Field id="task-instructions" label={t("jobTemplates.instructions")}>
            <Textarea
              id="task-instructions"
              name="instructions"
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
            />
          </Field>
          <Checkbox
            label={t("app.required")}
            checked={mandatory}
            onChange={(event) => setMandatory(event.target.checked)}
          />
          <ChecklistEditor
            items={checklist}
            onChange={setChecklist}
            taskIndex={(task?.sequence ?? 1) - 1}
          />
        </fieldset>
        {save.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(save.error)}
          </p>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" disabled={save.isPending} onClick={onClose}>
            {t("app.cancel")}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t("app.loading") : t("app.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
